import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import cls from "classname"
import { geoCentroid, geoDistance, geoGraticule, geoOrthographic, geoPath } from "d3-geo"
import { useTranslation } from "react-i18next"

import {
  AUTO_SPIN_LAMBDA,
  BRAND,
  CLIP_ANGLE,
  DEFAULT_SCALE,
  DRAG_THRESHOLD_PX,
  H,
  HORIZON_RAD,
  INITIAL_ROTATION,
  MAX_SCALE,
  MIN_SCALE,
  PALETTE,
  RAMP,
  RESUME_DELAY_MS,
  SCROLL_IDLE_MS,
  SPHERE_CX,
  SPHERE_TOP_Y,
  STEPS,
  W,
  ZOOM_EPSILON,
  ZOOM_STEP
} from "./constants"
import GlobeControls from "./GlobeControls"
import { useZoomTween } from "./hooks/useZoomTween"

import "./Globe.less"

// Orthographic globe, ported from Trial's live-view map (app/(app)/dashboard/_components/
// world-map.tsx). Idle it drifts; drag rotates it; clicking a country turns it to the camera.
// Countries are shaded by whichever metric the caller hands over (alert count on the dashboard,
// chargeback rate on Business Health), which is the one thing Trial does differently: it plots
// live visitor dots on uniform land instead.
//
// The geometry is a plain GeoJSON asset baked from world-atlas (public/world-countries.json),
// already carrying an ISO alpha-2 code per country, so nothing here parses TopoJSON or
// translates numeric country ids. d3-geo does the projection: a globe re-projects on every
// frame as it turns, so the paths cannot be pre-computed the way a flat map's can.

// Which ramp a metric wants depends on how its values are spread. Counts are dominated by one
// market, so they need the square root or every other country collapses onto the palest step;
// rates already sit in the same order of magnitude across countries, so linear keeps them
// honest and does not exaggerate the small ones.
const RAMP_SHAPE = {
  sqrt: (ratio) => Math.sqrt(ratio),
  linear: (ratio) => ratio
}

const stepFor = (value, max, shape) => {
  if (!value || !max) return 0
  return Math.min(STEPS, Math.max(1, Math.ceil(shape(value / max) * STEPS)))
}

const clampPhi = (phi) => Math.max(-90, Math.min(90, phi))

const Globe = ({
  data,
  theme,
  isMobile,
  ariaLabel,
  legendAriaLabel,
  renderTooltipValue,
  intensityScale = "sqrt"
}) => {
  const { t } = useTranslation()
  const dark = theme === "dark"
  const palette = dark ? PALETTE.dark : isMobile ? PALETTE.mobileLight : PALETTE.light
  const ramp = dark ? RAMP.dark : RAMP.light

  const [features, setFeatures] = useState(null)
  const [rotation, setRotation] = useState(INITIAL_ROTATION)
  const [isDragging, setIsDragging] = useState(false)
  const [hoveredCode, setHoveredCode] = useState(null)
  const [box, setBox] = useState({ w: 0, h: 0 })
  const [isAutoSpinning, setIsAutoSpinning] = useState(true)
  const { scale, target, scaleRef, targetRef, animateScale } = useZoomTween(
    DEFAULT_SCALE,
    MIN_SCALE,
    MAX_SCALE
  )

  const svgRef = useRef(null)
  const rotationRef = useRef(INITIAL_ROTATION)
  const dragRef = useRef(null)
  const isDraggingRef = useRef(false)
  const pendingRotationRef = useRef(null)
  const isScrollingRef = useRef(false)
  const velocityRef = useRef(AUTO_SPIN_LAMBDA)
  const resumeRef = useRef(null)
  // The explicit play/pause state, kept apart from the transient pauses that a drag or a scroll
  // cause, so releasing a drag never restarts a globe the user deliberately paused.
  const userPausedRef = useRef(false)

  useEffect(() => {
    rotationRef.current = rotation
  }, [rotation])

  useEffect(() => {
    let cancelled = false
    fetch(`${process.env.PUBLIC_URL || ""}/world-countries.json`)
      .then((res) => (res.ok ? res.json() : null))
      .then((fc) => {
        if (!cancelled && fc?.features) setFeatures(fc.features)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // The svg is `slice`-scaled to cover the stage, so viewBox units do not map onto element
  // percentages. Track the painted box and convert by hand, otherwise tooltips drift.
  useEffect(() => {
    const el = svgRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setBox((prev) =>
        Math.abs(prev.w - width) > 1 || Math.abs(prev.h - height) > 1
          ? { w: width, h: height }
          : prev
      )
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const scheduleResume = useCallback(() => {
    if (resumeRef.current !== null) clearTimeout(resumeRef.current)
    resumeRef.current = setTimeout(() => {
      if (!userPausedRef.current) velocityRef.current = AUTO_SPIN_LAMBDA
      resumeRef.current = null
    }, RESUME_DELAY_MS)
  }, [])

  const toggleAutoSpin = useCallback(() => {
    const paused = !userPausedRef.current
    userPausedRef.current = paused
    velocityRef.current = paused ? 0 : AUTO_SPIN_LAMBDA
    setIsAutoSpinning(!paused)
    if (paused && resumeRef.current !== null) {
      clearTimeout(resumeRef.current)
      resumeRef.current = null
    }
  }, [])

  // One rAF loop: while dragging it flushes the pending rotation, otherwise it applies the drift.
  useEffect(() => {
    let rafId = requestAnimationFrame(function loop() {
      if (isDraggingRef.current) {
        if (pendingRotationRef.current) {
          setRotation(pendingRotationRef.current)
          pendingRotationRef.current = null
        }
      } else if (!isScrollingRef.current && velocityRef.current !== 0) {
        const r = rotationRef.current
        setRotation({ lambda: r.lambda + velocityRef.current, phi: r.phi })
      }
      rafId = requestAnimationFrame(loop)
    })
    return () => {
      cancelAnimationFrame(rafId)
      if (resumeRef.current !== null) clearTimeout(resumeRef.current)
    }
  }, [])

  // Scrolling the page stops the drift and abandons any drag in flight, so the globe does not
  // keep spinning (or keep tracking the cursor) while it slides out of view.
  useEffect(() => {
    let timeoutId = null
    const onScroll = () => {
      isScrollingRef.current = true
      if (isDraggingRef.current) {
        isDraggingRef.current = false
        dragRef.current = null
        pendingRotationRef.current = null
        velocityRef.current = 0
        setIsDragging(false)
        scheduleResume()
      }
      if (timeoutId !== null) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        isScrollingRef.current = false
      }, SCROLL_IDLE_MS)
    }
    window.addEventListener("scroll", onScroll, { capture: true, passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll, true)
      if (timeoutId !== null) clearTimeout(timeoutId)
    }
  }, [scheduleResume])

  const { byCode, max } = useMemo(() => {
    const byCode = new Map()
    let max = 0
    for (const row of data ?? []) {
      if (!row?.code) continue
      byCode.set(row.code, row)
      if (row.value > max) max = row.value
    }
    return { byCode, max }
  }, [data])

  const sphereCy = SPHERE_TOP_Y + scale

  const projection = useMemo(
    () =>
      geoOrthographic()
        .scale(scale)
        .translate([SPHERE_CX, sphereCy])
        .rotate([rotation.lambda, rotation.phi, 0])
        .clipAngle(CLIP_ANGLE),
    [rotation, scale, sphereCy]
  )

  const pathGen = useMemo(() => geoPath(projection), [projection])
  const graticule = useMemo(() => geoGraticule().step([20, 20])(), [])
  // d3-geo can throw ("reading '0'") while clipping a line whose points all land on the clip
  // horizon at certain rotations. This runs every animation frame, so an uncaught throw crashes
  // the whole page. The globe is decorative: on the rare bad frame, drop that path (it reappears
  // the next frame as the sphere turns) rather than take the render down.
  const safePath = useMemo(
    () => (geom) => {
      try {
        return pathGen(geom) ?? ""
      } catch {
        return ""
      }
    },
    [pathGen]
  )
  const graticulePath = useMemo(() => safePath(graticule), [safePath, graticule])

  const centroids = useMemo(() => {
    const m = new Map()
    for (const f of features ?? []) {
      const code = f.properties?.code
      if (!code) continue
      const c = geoCentroid(f)
      if (Number.isFinite(c[0]) && Number.isFinite(c[1])) m.set(code, c)
    }
    return m
  }, [features])

  // Every country gets its own path so every country is hoverable, like Trial. The merged copy
  // underneath paints opaque land, so a low choropleth step blends with the land rather than
  // with the ocean showing through it.
  const { landPath, countries } = useMemo(() => {
    if (!features) return { landPath: "", countries: [] }
    const land = []
    const countries = []
    for (const f of features) {
      const d = safePath(f)
      if (!d) continue
      land.push(d)
      const code = f.properties?.code ?? null
      const row = code ? byCode.get(code) : null
      const step = stepFor(row?.value, max, RAMP_SHAPE[intensityScale] ?? RAMP_SHAPE.sqrt)
      countries.push({
        key: code ?? f.properties?.name,
        code,
        name: f.properties?.name,
        row,
        d,
        opacity: step ? ramp[step - 1] : 0
      })
    }
    return { landPath: land.join(" "), countries }
  }, [features, safePath, byCode, max, ramp, intensityScale])

  // Anchored to the country's centroid rather than the cursor, so it tracks the globe as it turns.
  const tooltip = useMemo(() => {
    if (!hoveredCode || isDragging) return null
    const centroid = centroids.get(hoveredCode)
    if (!centroid) return null
    const center = [-rotation.lambda, -rotation.phi]
    if (geoDistance(center, centroid) > HORIZON_RAD) return null
    let point
    try {
      point = projection(centroid)
    } catch {
      point = null
    }
    if (!point || !box.w || !box.h) return null
    // Undo the `slice` cover-scale to land on the country, not near it.
    const s = Math.max(box.w / W, box.h / H)
    const left = (box.w - W * s) / 2 + point[0] * s
    const top = (box.h - H * s) / 2 + point[1] * s
    const country = countries.find((c) => c.code === hoveredCode)
    // Anchor away from whichever edge is closest, so the box never falls out of the stage.
    return {
      name: country?.name ?? "",
      row: country?.row ?? null,
      left,
      top,
      flipX: left > box.w * 0.55,
      flipY: top > box.h * 0.65
    }
  }, [hoveredCode, isDragging, centroids, rotation, projection, box, countries])

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0) return
    isDraggingRef.current = true
    velocityRef.current = 0
    if (resumeRef.current !== null) {
      clearTimeout(resumeRef.current)
      resumeRef.current = null
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startLambda: rotationRef.current.lambda,
      startPhi: rotationRef.current.phi,
      moved: false
    }
  }, [])

  const onPointerMove = useCallback(
    (e) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== e.pointerId) return
      const dx = e.clientX - drag.startX
      const dy = e.clientY - drag.startY
      if (!drag.moved) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return
        drag.moved = true
        e.currentTarget.setPointerCapture(e.pointerId)
        setIsDragging(true)
      }
      // Sensitivity follows the sphere radius, so a drag moves the surface under the cursor.
      const sens = 180 / (Math.PI * scaleRef.current)
      pendingRotationRef.current = {
        lambda: drag.startLambda + dx * sens,
        phi: clampPhi(drag.startPhi - dy * sens)
      }
    },
    [scaleRef]
  )

  // Shared end-of-drag cleanup, called from both the SVG's own onPointerUp and the
  // window-level safety net below - guarded so a duplicate call (both firing for the
  // same release) is a no-op instead of double-scheduling the resume timer.
  const endDrag = useCallback(() => {
    if (!dragRef.current) return
    if (pendingRotationRef.current) {
      setRotation(pendingRotationRef.current)
      pendingRotationRef.current = null
    }
    dragRef.current = null
    isDraggingRef.current = false
    setIsDragging(false)
    scheduleResume()
  }, [scheduleResume])

  const onPointerUp = useCallback(
    (e) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== e.pointerId) return
      if (drag.moved && e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      endDrag()
    },
    [endDrag]
  )

  // Safety net: releasing the button while the cursor is outside the browser window (a fast
  // drag flicked off-screen, or the window losing focus mid-drag) can skip the SVG's own
  // pointerup/pointercancel entirely - the ref never resets, so the globe reads the next
  // pointermove as still dragging even though the button is up. A window-level listener force-
  // ends the drag regardless of which element, if any, actually received the release.
  useEffect(() => {
    const forceEndDrag = () => {
      if (isDraggingRef.current) endDrag()
    }
    window.addEventListener("pointerup", forceEndDrag)
    window.addEventListener("pointercancel", forceEndDrag)
    window.addEventListener("blur", forceEndDrag)
    return () => {
      window.removeEventListener("pointerup", forceEndDrag)
      window.removeEventListener("pointercancel", forceEndDrag)
      window.removeEventListener("blur", forceEndDrag)
    }
  }, [endDrag])

  // Compound off the in-flight target so rapid clicks accumulate.
  const zoomBy = useCallback(
    (factor) => animateScale(targetRef.current * factor),
    [animateScale, targetRef]
  )

  // Reset is a pure zoom action: it returns the globe to the default framing. Rotation is left to
  // auto-drift and drag, so reset never fights the spin, and it can gate on zoom alone.
  const reset = useCallback(() => {
    animateScale(DEFAULT_SCALE)
  }, [animateScale])

  // Read off the live tween target, so a button disables on the very click that hits the limit.
  const canZoomIn = target < MAX_SCALE - ZOOM_EPSILON
  const canZoomOut = target > MIN_SCALE + ZOOM_EPSILON
  // Reset only does something once you have zoomed in, so it disables at the default framing.
  const canReset = target > MIN_SCALE + ZOOM_EPSILON

  const controlsProps = {
    canZoomIn,
    canZoomOut,
    canReset,
    isAutoSpinning,
    onZoomIn: () => zoomBy(ZOOM_STEP),
    onZoomOut: () => zoomBy(1 / ZOOM_STEP),
    onReset: reset,
    onToggleAutoSpin: toggleAutoSpin
  }

  return (
    <>
      <div className="globe" data-dd-privacy="hidden">
        <svg
          ref={svgRef}
          className={cls("globe_canvas", { globe_canvas__grabbing: isDragging })}
          viewBox={`0 0 ${W} ${H}`}
          // `slice` fills the stage edge to edge instead of letterboxing it.
          preserveAspectRatio="xMidYMid slice"
          role="img"
          aria-label={ariaLabel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <defs>
            <radialGradient id="globe-ocean" cx="35%" cy="30%" r="80%">
              <stop offset="0%" stopColor={palette.sphereHighlight} stopOpacity={1} />
              <stop offset="100%" stopColor={palette.sphereShadow} stopOpacity={1} />
            </radialGradient>
            <radialGradient id="globe-rim" cx="50%" cy="50%" r="50%">
              <stop offset="85%" stopColor={BRAND} stopOpacity={0} />
              <stop offset="100%" stopColor={BRAND} stopOpacity={0.15} />
            </radialGradient>
          </defs>

          {/* Ocean (3D shading), then a faint brand atmosphere at the horizon. */}
          <circle cx={SPHERE_CX} cy={sphereCy} r={scale} fill="url(#globe-ocean)" />
          <circle
            cx={SPHERE_CX}
            cy={sphereCy}
            r={scale}
            fill="url(#globe-rim)"
            pointerEvents="none"
          />

          {/* A 20 degree grid is what actually sells the sphere as three-dimensional.
            Painted on the ocean only: the land below covers it. */}
          <path
            d={graticulePath}
            fill="none"
            stroke={palette.countryStroke}
            strokeWidth={0.5}
            strokeOpacity={palette.graticuleOpacity}
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />

          {/* Opaque land, so a pale choropleth step blends with the land and not the ocean. */}
          <path d={landPath} fill={palette.countryFill} pointerEvents="none" />

          <g>
            {countries.map((c) => {
              const hovered = !!c.code && hoveredCode === c.code
              return (
                <path
                  key={c.key}
                  data-code={c.code ?? undefined}
                  d={c.d}
                  fill={c.opacity ? BRAND : hovered ? palette.countryHoverFill : "transparent"}
                  fillOpacity={c.opacity || 1}
                  stroke={palette.countryStroke}
                  strokeWidth={hovered ? 1.2 : 0.6}
                  vectorEffect="non-scaling-stroke"
                  className="globe_country"
                  onPointerEnter={() => c.code && setHoveredCode(c.code)}
                  onPointerLeave={() => setHoveredCode((h) => (h === c.code ? null : h))}
                />
              )
            })}
          </g>
        </svg>

        {/* Controls and the intensity key share one right-hand rail, so both line up with the
          calendar above the globe. Mobile has no room for that overlay - its controls render
          below the globe entirely instead (see the fragment sibling below). */}
        {!isMobile && (
          <div className="globe-rail">
            <GlobeControls {...controlsProps} />

            <div className="globe-legend" role="img" aria-label={legendAriaLabel}>
              <span className="globe-legend_label">{t("More")}</span>
              <span className="globe-legend_bar" aria-hidden="true" />
              <span className="globe-legend_label">{t("Less")}</span>
            </div>
          </div>
        )}

        {tooltip && (
          <div
            className="globe_tooltip"
            style={{
              left: tooltip.left + (tooltip.flipX ? -12 : 12),
              top: tooltip.top + (tooltip.flipY ? -12 : 12),
              transform: `translate(${tooltip.flipX ? "-100%" : "0"}, ${tooltip.flipY ? "-100%" : "0"})`
            }}
          >
            <div className="globe_tooltip-title">{tooltip.name}</div>
            {tooltip.row && renderTooltipValue && (
              <div className="globe_tooltip-value">{renderTooltipValue(tooltip.row)}</div>
            )}
          </div>
        )}

        {/* Fades the sphere into the page so the KPI cards can sit on top of it. */}
        <div className="globe_fade" aria-hidden="true" />
      </div>

      {isMobile && <GlobeControls {...controlsProps} isMobile />}
    </>
  )
}

export default Globe
